import { UnauthorizedError } from '_core/api/client';
import { useApiClient } from '_core/api/context';
import { setUser } from '@sentry/minimal';
import {
  fetchMe,
  login,
  LoginRole,
  logout,
  ProfileSerialized,
  resetPasswordConfirm,
} from 'accounts/api';
import * as React from 'react';

interface ProfileContextValue {
  error: unknown;
  isFetched: boolean;
  profile: ProfileSerialized | null;
}

const ProfileContext = React.createContext<ProfileContextValue>({
  error: null,
  isFetched: false,
  profile: null,
});

if (__DEV__) {
  ProfileContext.displayName = 'ProfileContext';
}

export function useProfile() {
  return React.useContext(ProfileContext);
}

interface Props {
  children: React.ReactElement;
}

interface ContextValue {
  login: (email: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  resetPasswordConfirm: (
    uid: string,
    token: string,
    password: string
  ) => Promise<void>;
}

const AuthContext = React.createContext<ContextValue>({
  login: Promise.resolve,
  logout: Promise.resolve,
  resetPasswordConfirm: Promise.resolve,
});

if (__DEV__) {
  AuthContext.displayName = 'AuthContext';
}

export function AuthProvider({ children }: Props) {
  const api = useApiClient();
  const [error, setError] = React.useState<unknown>();
  const [isFetched, setIsFetched] = React.useState(false);
  const [profile, setProfile] = React.useState<ProfileSerialized | null>(null);

  React.useEffect(() => {
    setError(null);

    (async () => {
      try {
        setProfile(await fetchMe(api));
        setIsFetched(true);
      } catch (err: unknown) {
        if (err instanceof UnauthorizedError) {
          setIsFetched(true);
          return;
        }

        setError(err);
        setIsFetched(true);
        throw err;
      }
    })();
  }, [api]);

  React.useEffect(() => {
    setUser(profile && { email: profile.email });
  }, [profile]);

  const authContextValue = React.useMemo(
    (): ContextValue => ({
      login: async (email, password) => {
        const response = await login(api, LoginRole.Customer, email, password);

        setProfile(response.profile);
      },

      logout: async () => {
        setProfile(null);
        await logout(api);
      },

      resetPasswordConfirm: async (uid, token, password) => {
        const response = await resetPasswordConfirm(api, uid, token, password);

        setProfile(response.profile);
      },
    }),

    [api]
  );

  const profileContextValue = React.useMemo(
    (): ProfileContextValue => ({ error, isFetched, profile }),
    [error, isFetched, profile]
  );

  return (
    <AuthContext.Provider value={authContextValue}>
      <ProfileContext.Provider value={profileContextValue}>
        {children}
      </ProfileContext.Provider>
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return React.useContext(AuthContext);
}
